import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import BulkNudgeReuploadDialog from "../components/BulkNudgeReuploadDialog";
import {
  ShieldCheck, ShieldWarning, Warning, MagnifyingGlass, CheckCircle, XCircle, ArrowRight, FileText, Funnel, Envelope,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  compliant: { label: "Compliant", icon: CheckCircle, cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/40" },
  warning: { label: "Warning", icon: Warning, cls: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-900/40" },
  non_compliant: { label: "Non-Compliant", icon: XCircle, cls: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/40" },
};

export default function SupplierCompliancePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showBulkNudge, setShowBulkNudge] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await API.get("/suppliers/compliance-overview");
        setData(d);
      } catch { toast.error("Failed to load compliance data"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const { overview, suppliers } = data;
  const filtered = suppliers
    .filter(s => filter === "all" || s.status === filter)
    .filter(s => s.company_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="supplier-compliance-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Supplier Compliance</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Overview of supplier document compliance status</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowBulkNudge(true)}
          className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300"
          data-testid="bulk-nudge-open-btn"
          title="Email every supplier whose compliance docs have no file behind them"
        >
          <Envelope size={14} className="mr-1" /> Email all missing files
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="compliance-summary">
        <SummaryCard label="Total Suppliers" value={overview.total} icon={FileText} color="primary" />
        <SummaryCard label="Compliant" value={overview.compliant} icon={CheckCircle} color="emerald" onClick={() => setFilter("compliant")} />
        <SummaryCard label="Warning" value={overview.warning} icon={Warning} color="amber" onClick={() => setFilter("warning")} />
        <SummaryCard label="Non-Compliant" value={overview.non_compliant} icon={XCircle} color="red" onClick={() => setFilter("non_compliant")} />
      </div>

      {/* Compliance Rate Bar */}
      <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="compliance-rate">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold font-[Chivo]">Overall Compliance Rate</p>
          <p className="text-lg font-bold font-[Chivo]">{overview.compliance_rate}%</p>
        </div>
        <Progress value={overview.compliance_rate} className="h-2" />
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search suppliers..." className="pl-9 text-xs" value={search} onChange={e => setSearch(e.target.value)} data-testid="compliance-search" />
        </div>
        <div className="flex gap-1.5">
          {["all", "compliant", "warning", "non_compliant"].map(f => (
            <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" className="text-[10px] h-8" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
              {f === "all" ? "All" : f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            </Button>
          ))}
        </div>
      </div>

      {/* Supplier List */}
      <div className="space-y-2" data-testid="compliance-list">
        {filtered.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <ShieldCheck size={28} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No suppliers match your filter</p>
          </div>
        ) : (
          filtered.map(s => {
            const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.non_compliant;
            const Icon = cfg.icon;
            return (
              <div key={s.supplier_id}
                className="flex items-center justify-between p-3.5 border border-border rounded-lg bg-card hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/vendors/${s.supplier_id}`)}
                data-testid={`compliance-row-${s.supplier_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                    <Icon size={18} className={cfg.cls} weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{s.company_name}</p>
                    <p className="text-[10px] text-muted-foreground">{s.trade || "No trade"} - {s.total_documents} docs</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {s.manually_compliant && <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Manual Override</Badge>}
                  {s.missing_documents.length > 0 && (
                    <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">{s.missing_documents.length} missing</Badge>
                  )}
                  {s.has_expired && <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">Expired docs</Badge>}
                  {s.has_expiring && !s.has_expired && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Expiring</Badge>}
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${cfg.bg} ${cfg.cls}`}>{cfg.label}</span>
                  <ArrowRight size={14} className="text-muted-foreground" />
                </div>
              </div>
            );
          })
        )}
      </div>

      <BulkNudgeReuploadDialog
        open={showBulkNudge}
        onClose={() => setShowBulkNudge(false)}
        onSent={() => { /* the overview rates don't change immediately — page refresh isn't needed */ }}
      />
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, onClick }) {
  const colors = {
    primary: "border-primary/20",
    emerald: "border-emerald-200 dark:border-emerald-800",
    amber: "border-amber-200 dark:border-amber-800",
    red: "border-red-200 dark:border-red-800",
  };
  const iconColors = {
    primary: "text-primary",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    red: "text-red-600 dark:text-red-400",
  };
  return (
    <div className={`border rounded-lg p-4 bg-card ${colors[color]} ${onClick ? "cursor-pointer hover:bg-muted/30" : ""} transition-colors`}
      onClick={onClick} data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} className={iconColors[color]} weight="duotone" />
      </div>
      <p className="text-2xl font-bold font-[Chivo]">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}
